import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  // Truck removed - using image asset instead
  Edit, 
  Trash2,
  Calendar,
  Hash,
  Tag,
  Building,
  MapPin,
  Activity,
  Gauge,
  Settings,
  Camera,
  History,
  Download,
  Package,
  ExternalLink,
  Wrench,
  FileCheck
} from 'lucide-react';
import ImageUploader from '../components/ImageUploader';
import IntegerInput from '../components/common/IntegerInput';
import MaintenanceModal from '../components/MaintenanceModal';
import { trucksService } from '../services/trucksService';
import { maintenanceService } from '../services/maintenanceService';

const TruckDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNewTruckMode = window.location.pathname.endsWith('/new') || id === 'new';
  
  const [truck, setTruck] = useState(null);
  const [exits, setExits] = useState([]);
  const [exitsPagination, setExitsPagination] = useState(null);
  const [exitsPage, setExitsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingExits, setLoadingExits] = useState(false);
  const [error, setError] = useState(null);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  // Cargar datos del truck o inicializar formulario para nueva unidad
  useEffect(() => {
    const loadTruckData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Cargando detalles del truck ID:', id, 'isNewTruckMode:', isNewTruckMode);

        // Verificar si estamos en modo de nueva unidad (usando la URL o el ID)
        if (isNewTruckMode) {
          // Crear un nuevo truck (formulario vacío)
          console.log('🚚 Creando nuevo truck');
          setIsEditing(true);
          
          // Datos por defecto para un nuevo truck
          const newTruckData = {
            numero_economico: '',
            placas: '',
            serie: '',
            marca: '',
            modelo: '',
            anio: new Date().getFullYear(),
            tipo_unidad: 'Tractocamion',
            motor: '',
            km_actual: 0,
            km_prox_servicio: 0,
            estatus: 'Activo',
            fecha_alta: new Date().toISOString().split('T')[0],
            fecha_baja: '',
            empresa: 'TITSA',
            base: '',
            observaciones: ''
          };
          
          setTruck(newTruckData);
          setEditForm({...newTruckData});
        } else {
          // Verificar si tenemos un ID válido para cargar un truck existente
          if (!id || id === 'undefined') {
            console.error('❌ Error: ID de unidad no válido o no proporcionado:', id);
            setError('ID de unidad no válido. Regrese a la página de flotilla e intente nuevamente.');
            return; // Salir temprano para evitar llamadas API con ID inválido
          }
          
          // Cargar truck existente
          const truckData = await trucksService.getById(id);
          console.log('🚚 Truck cargado:', truckData);
          
          // Convertir valor de estatus de backend a frontend si es necesario
          if (truckData && truckData.estatus === 'En mantenimiento') {
            // Por defecto, mostrar como preventivo (se puede mejorar esto guardando el tipo real en otro campo)
            truckData.estatus = 'Mantenimiento preventivo';
          }
          
          setTruck(truckData);
          
          // Inicializar formulario de edición con todos los campos incluyendo los recién agregados
          setEditForm({
            numero_economico: truckData.numero_economico || '',
            placas: truckData.placas || '',
            serie: truckData.serie || '',
            marca: truckData.marca || '',
            modelo: truckData.modelo || '',
            anio: truckData.anio || '',
            tipo_unidad: truckData.tipo_unidad || '',
            motor: truckData.motor || '',
            km_actual: truckData.km_actual || 0,
            km_prox_servicio: truckData.km_prox_servicio || 0,
            estatus: truckData.estatus || '',
            fecha_alta: truckData.fecha_alta ? new Date(truckData.fecha_alta).toISOString().split('T')[0] : '',
            fecha_baja: truckData.fecha_baja ? new Date(truckData.fecha_baja).toISOString().split('T')[0] : '',
            empresa: truckData.empresa || '',
            base: truckData.base || '',
            observaciones: truckData.observaciones || ''
          });

          // Cargar historial de salidas
          await loadExits(1);
        }
      } catch (error) {
        console.error('❌ Error cargando truck:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadTruckData();
  }, [id, isNewTruckMode]);

  // Función separada para cargar salidas con paginación
  const loadExits = async (page = 1) => {
    // CORRECCIÓN: Evitar cargar salidas si estamos creando un nuevo truck o si el ID es undefined
    // Esto previene errores 404 cuando se navega a /trucks/new o cuando el ID no es válido
    if (isNewTruckMode || !id || id === 'undefined') {
      console.log('🚚 Omitiendo carga de salidas: Modo nueva unidad o ID inválido');
      setExits([]);
      setExitsPagination(null);
      setLoadingExits(false);
      return;
    }
    
    try {
      setLoadingExits(true);
      console.log('🔄 Cargando salidas para truck ID:', id);
      const result = await trucksService.getTruckExits(id, page, 20);
      setExits(result.data || []);
      setExitsPagination(result.pagination);
      setExitsPage(page);
    } catch (exitsError) {
      console.warn('⚠️ Error cargando salidas:', exitsError);
      setExits([]);
      setExitsPagination(null);
    } finally {
      setLoadingExits(false);
    }
  };

  const handleGoBack = () => {
    navigate('/trucks');
  };

  const handleImageUpload = async (imageUrl) => {
    try {
      setIsUploading(true);
      const updatedTruck = await trucksService.updateImage(id, imageUrl);
      setTruck(updatedTruck);
      setShowImageUploader(false);
      console.log('✅ Imagen actualizada:', updatedTruck);
    } catch (error) {
      console.error('❌ Error actualizando imagen:', error);
      setError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    
    // Si estamos en modo de nueva unidad o truck es nulo, usar valores por defecto
    if (isNewTruckMode || !truck) {
      // Datos por defecto para un nuevo truck
      const newTruckData = {
        numero_economico: '',
        placas: '',
        serie: '',
        marca: '',
        modelo: '',
        anio: new Date().getFullYear(),
        tipo_unidad: 'Tractocamion',
        motor: '',
        km_actual: 0,
        km_prox_servicio: 0,
        estatus: 'Activo',
        fecha_alta: new Date().toISOString().split('T')[0],
        fecha_baja: '',
        empresa: 'TITSA',
        base: '',
        observaciones: ''
      };
      
      setEditForm({...newTruckData});
      return;
    }
    
    // Restaurar valores originales con formatos adecuados para fechas
    setEditForm({
      numero_economico: truck.numero_economico || '',
      placas: truck.placas || '',
      serie: truck.serie || '',
      marca: truck.marca || '',
      modelo: truck.modelo || '',
      anio: truck.anio || new Date().getFullYear(),
      tipo_unidad: truck.tipo_unidad || '',
      motor: truck.motor || '',
      km_actual: truck.km_actual || 0,
      km_prox_servicio: truck.km_prox_servicio || 0,
      estatus: truck.estatus || '',
      fecha_alta: truck.fecha_alta ? new Date(truck.fecha_alta).toISOString().split('T')[0] : '',
      fecha_baja: truck.fecha_baja ? new Date(truck.fecha_baja).toISOString().split('T')[0] : '',
      empresa: truck.empresa || '',
      base: truck.base || '',
      observaciones: truck.observaciones || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // Guardar el valor de estatus seleccionado en el frontend antes de la actualización
      const selectedStatus = editForm.estatus;
      
      // Validar que km_prox_servicio sea mayor que km_actual
      if (
        editForm.km_prox_servicio !== undefined &&
        editForm.km_actual !== undefined &&
        parseInt(editForm.km_prox_servicio) <= parseInt(editForm.km_actual)
      ) {
        setError('El kilometraje del próximo servicio debe ser mayor que el kilometraje actual');
        setLoading(false);
        return;
      }
      
      let updatedTruck;
      
      if (isNewTruckMode) {
        // Crear un nuevo truck
        console.log('🚚 Creando nueva unidad con datos:', editForm);
        
        // Validar campos obligatorios
        if (!editForm.numero_economico || editForm.numero_economico.trim() === '') {
          setError('El número económico es obligatorio');
          setLoading(false);
          return;
        }
        
        // Validar que el número de serie no esté vacío
        if (!editForm.serie || editForm.serie.trim() === '') {
          setError('El número de serie/VIN es obligatorio');
          setLoading(false);
          return;
        }
        
        try {
          updatedTruck = await trucksService.create(editForm);
          console.log('✅ Truck creado:', updatedTruck);
          
          // Navegar al detalle del nuevo truck, pero con un pequeño retraso
          // para permitir que el estado se actualice correctamente
          if (updatedTruck && updatedTruck.id) {
            console.log('🚚 Navegando a la nueva unidad con ID:', updatedTruck.id);
            
            // Establecer el nuevo truck antes de navegar
            setTruck(updatedTruck);
            setIsEditing(false);
            setError(null); // Limpiar cualquier error previo
            
            // Retrasar la navegación ligeramente para permitir que React actualice el estado
            setTimeout(() => {
              navigate(`/trucks/${updatedTruck.id}`, { replace: true });
            }, 100);
            
            return; // Evitar continuar con el código ya que vamos a navegar
          }
        } catch (createError) {
          console.error('❌ Error al crear truck:', createError);
          
          // Mostrar el error pero seguir en modo edición
          setError(createError.message || 'Error al crear la unidad');
          setLoading(false);
          return; // Salir temprano para mantener al usuario en el formulario
        }
      } else {
        // Actualizar un truck existente
        try {
          updatedTruck = await trucksService.update(id, editForm);
          console.log('✅ Truck actualizado:', updatedTruck);
        } catch (updateError) {
          console.error('❌ Error al actualizar truck:', updateError);
          setError(updateError.message || 'Error al actualizar la unidad');
          setLoading(false);
          return;
        }
      }
      
      // Mantener el valor de estatus del frontend
      if (updatedTruck) {
        // Si el backend devolvió "En mantenimiento", restaurar el valor específico que seleccionó el usuario
        if (updatedTruck.estatus === 'En mantenimiento') {
          updatedTruck.estatus = selectedStatus;
        }
      }
      
      setTruck(updatedTruck);
      setIsEditing(false);
      setError(null); // Limpiar cualquier error previo
    } catch (error) {
      console.error('❌ Error al guardar truck:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Campos que siempre deben ser numéricos
    const numericFields = ['km_actual', 'km_prox_servicio', 'km_mantenimiento', 'anio'];
    
    // Verificar si es un campo numérico
    if (numericFields.includes(field)) {
      // Si el valor está vacío, usamos 0 como valor por defecto
      // De lo contrario, convertimos a número o mantenemos como 0 si no es válido
      const numericValue = value === '' ? 0 : (isNaN(Number(value)) ? 0 : Number(value));
      
      setEditForm(prev => ({
        ...prev,
        [field]: numericValue
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDelete = async () => {
    try {
      await trucksService.delete(id);
      navigate('/trucks');
    } catch (error) {
      console.error('❌ Error eliminando truck:', error);
      setError(error.message);
    }
  };

  // Función para obtener el color del estatus
  const getStatusColor = (estatus) => {
    switch (estatus) {
      case 'Activo':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Mantenimiento preventivo':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Mantenimiento correctivo':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'En mantenimiento': // Mantener compatibilidad con registros antiguos
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Baja':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Función para obtener el color de la empresa
  const getCompanyColor = (empresa) => {
    switch (empresa) {
      case 'TITSA':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TGT':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'GTT':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'GIFSA':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'SECSA':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'JCB':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INTERTRANSPORT':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'LIENZO CHARRO':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'MUSTANG TRUCKING':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'STALLIONS':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        // Generar un color pseudo-aleatorio pero consistente basado en el nombre de la empresa
        const hash = empresa?.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) || 0;
        const colorChoices = [
          'bg-sky-100 text-sky-800 border-sky-200',
          'bg-teal-100 text-teal-800 border-teal-200',
          'bg-lime-100 text-lime-800 border-lime-200',
          'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
          'bg-pink-100 text-pink-800 border-pink-200',
          'bg-rose-100 text-rose-800 border-rose-200'
        ];
        return colorChoices[hash % colorChoices.length] || 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Helper: devuelve ruta y alt de imagen según el tipo de unidad
  const imageForTipo = (tipo) => {
    if (!tipo) return { src: '/images/tractocamion.png', alt: 'Tracto camión' };
    const t = String(tipo).toLowerCase();
    if (t.includes('dolly')) return { src: '/images/dolly.png', alt: 'Dolly' };
    if (t.includes('auto') && t.includes('tanque')) return { src: '/images/auto_tanque.png', alt: 'Auto tanque' };
    if (t.includes('caja') || t.includes('cajaseca') || t.includes('caja seca') || t.includes('caja_seca')) return { src: '/images/caja_seca.png', alt: 'Caja seca' };
    if (t.includes('gas') || t.includes('unidad de gasolina') || t.includes('gasolina') || t.includes('unidades_gas')) return { src: '/images/unidades_gas.png', alt: 'Unidad de gasolina' };
    if (t.includes('semi') || t.includes('remolque') || t.includes('semi remolque') || t.includes('semiremolque')) return { src: '/images/semiremolque.png', alt: 'Semi remolque' };
    if (t.includes('grua') || t.includes('grúa')) return { src: '/images/grua.png', alt: 'Grúa' };
    if (t.includes('tracto') || t.includes('camion') || t.includes('camión') || t.includes('tractocamion')) return { src: '/images/tractocamion.png', alt: 'Tracto camión' };
    // fallback
    return { src: '/images/tractocamion.png', alt: 'Tracto camión' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-xl text-gray-600">Cargando unidad...</p>
        </div>
      </div>
    );
  }

  // Redireccionar solo en caso de errores fatales (ID inválido o unidad no encontrada)
  if (error && !isEditing && error.includes('ID de unidad no válido')) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-xl max-w-md">
          <div className="text-5xl text-red-500 mb-4">⚠️</div>
          <p className="text-xl text-red-600 mb-4">Error: {error}</p>
          <button
            onClick={handleGoBack}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Volver a Flotilla
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Mensajes de error durante la edición */}
        {error && isEditing && (
          <motion.div 
            className={`${error.includes('ERROR:') ? 'bg-red-100 border-red-500' : 'bg-yellow-100 border-yellow-500'} border-l-4 p-4 mb-6 rounded shadow-md flex justify-between items-center`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div>
              <p className={`font-bold ${error.includes('ERROR:') ? 'text-red-700' : 'text-yellow-700'}`}>
                {error.includes('ERROR:') ? 'ERROR' : 'AVISO'}
              </p>
              <p className={error.includes('ERROR:') ? 'text-red-700' : 'text-yellow-700'}>
                {error.replace('ERROR: ', '')}
              </p>
              {error.includes('serie/VIN') && (
                <p className="text-red-700 font-bold mt-1">
                  Cada unidad debe tener un número de serie/VIN único en el sistema.
                </p>
              )}
            </div>
            <button 
              onClick={() => setError(null)} 
              className={`${error.includes('ERROR:') ? 'text-red-500 hover:text-red-700' : 'text-yellow-500 hover:text-yellow-700'}`}
              title="Cerrar mensaje"
            >
              ✖
            </button>
          </motion.div>
        )}
        {/* Título diferente para nueva unidad */}
        {isNewTruckMode && (
          <motion.h1
            className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <img
              src="/images/tractocamion.png"
              alt="Nueva Unidad"
              className="w-8 h-8 object-contain text-orange-600"
            />
            Agregar Nueva Unidad
          </motion.h1>
        )}
        {/* Header con botón de regreso y acciones */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-800 font-medium transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a Flotilla
          </button>
          
          <div className="flex items-center gap-3">
            {isNewTruckMode || isEditing ? (
              <>
                <motion.button
                  onClick={handleSaveEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="w-4 h-4" />
                  {isNewTruckMode ? 'Crear Unidad' : 'Guardar Cambios'}
                </motion.button>
                <motion.button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancelar
                </motion.button>
              </>
            ) : (
              <>
                <motion.button
                  onClick={handleEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </motion.button>
                <motion.button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </motion.button>
                
                <motion.button
                  onClick={() => setShowMaintenanceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Wrench className="w-4 h-4" />
                  Generar Mantenimiento
                </motion.button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Columna izquierda - Imagen (oculta para nueva unidad) */}
          {!isNewTruckMode && (
            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden mb-4 relative group">
                  {truck?.image_url ? (
                    <img
                      src={trucksService.getImageUrl(truck.image_url)}
                      alt={truck.numero_economico}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <img 
                                src={isEditing ? imageForTipo(editForm.tipo_unidad).src : imageForTipo(truck?.tipo_unidad).src} 
                                alt={isEditing ? imageForTipo(editForm.tipo_unidad).alt : imageForTipo(truck?.tipo_unidad).alt} 
                                className="w-24 h-24 text-gray-400 object-contain" 
                              />
                            </div>
                  )}
                  
                  <button
                    onClick={() => setShowImageUploader(true)}
                    className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  >
                    <Camera className="w-8 h-8 text-white" />
                  </button>
                </div>
                
                <button
                  onClick={() => setShowImageUploader(true)}
                  className="w-full bg-orange-600 text-white py-3 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                  disabled={isUploading}
                >
                  <Camera className="w-5 h-5" />
                  {isUploading ? 'Subiendo...' : 'Cambiar Imagen'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Columna derecha - Información */}
          <motion.div
            className={isNewTruckMode ? "lg:col-span-3 space-y-6" : "lg:col-span-2 space-y-6"}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Información básica del truck */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <img 
                  src={isEditing ? imageForTipo(editForm.tipo_unidad).src : imageForTipo(truck?.tipo_unidad).src} 
                  alt={isEditing ? imageForTipo(editForm.tipo_unidad).alt : imageForTipo(truck?.tipo_unidad).alt} 
                  className="w-6 h-6 object-contain text-orange-600" 
                />
                {isEditing ? (
                  <div className="flex flex-col">
                    <input
                      type="text"
                      value={editForm.numero_economico}
                      onChange={(e) => handleInputChange('numero_economico', e.target.value)}
                      className="text-2xl font-bold text-gray-800 bg-gray-50 border border-gray-300 rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Número económico"
                    />
                  </div>
                ) : (
                  truck.numero_economico
                )}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Placas</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.placas}
                          onChange={(e) => handleInputChange('placas', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      ) : (
                        <p className="font-semibold">{truck.placas || 'No disponible'}</p>
                      )}
                    </div>
                  </div>

                    <div className="flex items-center gap-3">
                    <Tag className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <p className="text-sm text-gray-500">Serie/VIN</p>
                        {isEditing && (
                          <span className="text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded font-bold">
                            * Debe ser único
                          </span>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="flex flex-col">
                          <input
                            type="text"
                            value={editForm.serie}
                            onChange={(e) => handleInputChange('serie', e.target.value)}
                            className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="Número de serie/VIN único"
                          />
                          <small className="text-xs text-red-600 mt-1 font-bold">
                            * El número de serie/VIN debe ser único en todo el sistema
                          </small>
                        </div>
                      ) : (
                        <p className="font-semibold">{truck.serie || 'No disponible'}</p>
                      )}
                    </div>
                  </div>                  <div className="flex items-center gap-3">
                    <img src={imageForTipo(truck?.tipo_unidad).src} alt={imageForTipo(truck?.tipo_unidad).alt} className="w-5 h-5 text-gray-500 object-contain" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Tipo de unidad</p>
                      {isEditing ? (
                        <select
                          value={editForm.tipo_unidad}
                          onChange={(e) => handleInputChange('tipo_unidad', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          <option value="">Seleccionar tipo</option>
                          {trucksService.getTipoUnidadOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="font-semibold">{truck.tipo_unidad || 'No especificado'}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <img src={imageForTipo(truck?.tipo_unidad).src} alt={imageForTipo(truck?.tipo_unidad).alt} className="w-5 h-5 text-gray-500 object-contain" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Marca</p>
                      {isEditing ? (
                        <div>
                          <div className="flex">
                            <input
                              type="text"
                              list="marcas"
                              value={editForm.marca}
                              onChange={(e) => handleInputChange('marca', e.target.value)}
                              className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="Seleccionar o escribir marca"
                            />
                          </div>
                          <datalist id="marcas">
                            <option value="">Seleccionar marca</option>
                            {trucksService.getMarcaOptions().map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </datalist>
                          <small className="text-xs text-gray-500">Puede seleccionar de la lista o escribir una marca personalizada</small>
                        </div>
                      ) : (
                        <p className="font-semibold">{truck.marca || 'No especificada'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Modelo</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.modelo}
                          onChange={(e) => handleInputChange('modelo', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      ) : (
                        <p className="font-semibold">{truck.modelo || 'No especificado'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Año</p>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editForm.anio}
                          onChange={(e) => handleInputChange('anio', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      ) : (
                        <p className="font-semibold">{truck.anio || 'No especificado'}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Building className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Empresa</p>
                      {isEditing ? (
                        <div>
                          <div className="flex">
                            <input
                              type="text"
                              list="empresas"
                              value={editForm.empresa}
                              onChange={(e) => handleInputChange('empresa', e.target.value)}
                              className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="Seleccionar o escribir empresa"
                            />
                          </div>
                          <datalist id="empresas">
                            <option value="">Seleccionar empresa</option>
                            {trucksService.getEmpresaOptions().map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </datalist>
                          <small className="text-xs text-gray-500">Puede seleccionar de la lista o escribir una empresa personalizada</small>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getCompanyColor(truck.empresa)}`}>
                          {truck.empresa}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Base</p>
                      {isEditing ? (
                        <div>
                          <div className="flex">
                            <input
                              type="text"
                              list="bases"
                              value={editForm.base}
                              onChange={(e) => handleInputChange('base', e.target.value)}
                              className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                              placeholder="Seleccionar o escribir base"
                            />
                          </div>
                          <datalist id="bases">
                            <option value="">Seleccionar base</option>
                            {trucksService.getBaseOptions().map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </datalist>
                          <small className="text-xs text-gray-500">Puede seleccionar de la lista o escribir una base personalizada</small>
                        </div>
                      ) : (
                        <p className="font-semibold">{truck.base || 'No especificada'}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Estatus</p>
                      {isEditing ? (
                        <select
                          value={editForm.estatus}
                          onChange={(e) => handleInputChange('estatus', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        >
                          {trucksService.getEstatusOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(truck.estatus)}`}>
                          {truck.estatus}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Gauge className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Kilometraje Actual</p>
                      {isEditing ? (
                        <IntegerInput
                          name="km_actual"
                          value={editForm.km_actual}
                          onChange={(e) => handleInputChange('km_actual', e.target.value)}
                          className="w-full font-semibold text-xl text-orange-600 bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="Ingrese kilometraje actual"
                        />
                      ) : (
                        <p className="font-semibold text-xl text-orange-600">{truck.km_actual?.toLocaleString() || '0'} km</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Próximo Servicio</p>
                      {isEditing ? (
                        <IntegerInput
                          name="km_prox_servicio"
                          value={editForm.km_prox_servicio}
                          onChange={(e) => handleInputChange('km_prox_servicio', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                          placeholder="Ingrese kilometraje próximo servicio"
                          validation={{
                            rules: {
                              compare: (val) => val <= parseInt(editForm.km_actual || 0)
                            },
                            errorMsg: 'El kilometraje próximo debe ser mayor que el kilometraje actual'
                          }}
                        />
                      ) : (
                        <p className="font-semibold">{truck.km_prox_servicio?.toLocaleString() || '0'} km</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-gray-500" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Motor</p>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.motor}
                          onChange={(e) => handleInputChange('motor', e.target.value)}
                          className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      ) : (
                        <p className="font-semibold">{truck.motor || 'No disponible'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fechas importantes */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-800 mb-2">Fechas importantes</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Fecha de alta</p>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editForm.fecha_alta || ''}
                        onChange={(e) => handleInputChange('fecha_alta', e.target.value)}
                        className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    ) : (
                      <p className="font-semibold text-gray-600">
                        {truck.fecha_alta ? (() => {
                          const date = new Date(truck.fecha_alta);
                          const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                          const localDate = new Date(date.getTime() + userTimezoneOffset);
                          return localDate.toLocaleDateString();
                        })() : 'No registrada'}
                      </p>
                    )}
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Fecha de baja</p>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editForm.fecha_baja || ''}
                        onChange={(e) => handleInputChange('fecha_baja', e.target.value)}
                        className="w-full font-semibold bg-gray-50 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    ) : (
                      <p className="font-semibold text-gray-600">
                        {truck.fecha_baja ? (() => {
                          const date = new Date(truck.fecha_baja);
                          const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                          const localDate = new Date(date.getTime() + userTimezoneOffset);
                          return localDate.toLocaleDateString();
                        })() : 'No registrada'}
                      </p>
                    )}
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Registro en sistema</p>
                    <p className="font-semibold text-gray-600">
                      {truck.created_at ? new Date(truck.created_at).toLocaleString() : 'No disponible'}
                    </p>
                    {truck.updated_at && truck.updated_at !== truck.created_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Última actualización: {new Date(truck.updated_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                {/* Observaciones */}
                <h4 className="font-semibold text-gray-800 mb-2">Observaciones</h4>
                {isEditing ? (
                  <textarea
                    value={editForm.observaciones}
                    onChange={(e) => handleInputChange('observaciones', e.target.value)}
                    rows={3}
                    className="w-full text-gray-600 bg-gray-50 border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Observaciones sobre la unidad..."
                  />
                ) : (
                  <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">
                    {truck.observaciones || 'Sin observaciones'}
                  </p>
                )}
              </div>
            </div>

            {/* Historial de refacciones - Solo para unidades existentes */}
            {!isNewTruckMode && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <History className="w-6 h-6 text-orange-600" />
                  Historial de Refacciones
                  {exitsPagination && (
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm font-medium">
                      {exitsPagination.totalItems}
                    </span>
                  )}
                </h3>

              {loadingExits ? (
                <div className="text-center py-8">
                  <motion.div
                    className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <p className="text-gray-600">Cargando salidas...</p>
                </div>
              ) : exits.length > 0 ? (
                <>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {exits.map((exit, index) => (
                      <motion.div
                        key={exit.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-all duration-200 group"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => navigate(`/exits/exit/${exit.id}`)}
                        whileHover={{ scale: 1.02 }}
                        title="Clic para ver detalles completos de la salida"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Package className="w-4 h-4 text-gray-500" />
                              <span className="font-semibold text-gray-800">
                                {exit.product_name || 'Producto eliminado'}
                              </span>
                              <span className="text-gray-500">
                                (SKU: {exit.product_sku || 'N/A'})
                              </span>
                              <div className="flex items-center gap-2 ml-auto">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                  ID: {exit.id}
                                </span>
                                <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Cantidad:</span> {exit.quantity}
                              </div>
                              <div>
                                <span className="font-medium">Costo unitario:</span> ${exit.unit_cost || 'N/A'}
                              </div>
                              <div>
                                <span className="font-medium">Fecha:</span> {(() => {
                                  const date = new Date(exit.exit_date);
                                  const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                                  const localDate = new Date(date.getTime() + userTimezoneOffset);
                                  return localDate.toLocaleDateString();
                                })()}
                              </div>
                              <div>
                                <span className="font-medium">Unidad:</span> {exit.truck}
                              </div>
                              {exit.solicitante && (
                                <div>
                                  <span className="font-medium">Solicitante:</span> {exit.solicitante}
                                </div>
                              )}
                              {exit.workshop && (
                                <div>
                                  <span className="font-medium">Taller:</span> {exit.workshop}
                                </div>
                              )}
                            </div>
                            {exit.reason && (
                              <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">Razón:</span> {exit.reason}
                              </div>
                            )}
                            {exit.notes && (
                              <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">Notas:</span> {exit.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Paginación */}
                  {exitsPagination && exitsPagination.totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-600">
                        Página {exitsPagination.currentPage} de {exitsPagination.totalPages}
                        {' '}({exitsPagination.totalItems} salidas total)
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => loadExits(exitsPage - 1)}
                          disabled={exitsPage <= 1}
                          className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        
                        {/* Páginas */}
                        {Array.from({ length: Math.min(5, exitsPagination.totalPages) }, (_, i) => {
                          let pageNum;
                          if (exitsPagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (exitsPage <= 3) {
                            pageNum = i + 1;
                          } else if (exitsPage >= exitsPagination.totalPages - 2) {
                            pageNum = exitsPagination.totalPages - 4 + i;
                          } else {
                            pageNum = exitsPage - 2 + i;
                          }
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => loadExits(pageNum)}
                              className={`px-3 py-1 text-sm rounded ${
                                pageNum === exitsPage
                                  ? 'bg-orange-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => loadExits(exitsPage + 1)}
                          disabled={exitsPage >= exitsPagination.totalPages}
                          className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay refacciones registradas para esta unidad</p>
                </div>
              )}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Modal de subida de imagen - No mostrar para nueva unidad */}
      <AnimatePresence>
        {showImageUploader && !isNewTruckMode && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">Cambiar Imagen de Unidad</h3>
              <ImageUploader
                onImageUpload={handleImageUpload}
                currentImage={truck?.image_url ? trucksService.getImageUrl(truck.image_url) : null}
                onCancel={() => setShowImageUploader(false)}
                type="truck"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de confirmación de eliminación - No mostrar para nueva unidad */}
      <AnimatePresence>
        {showDeleteModal && !isNewTruckMode && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">Confirmar Eliminación</h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que deseas eliminar la unidad <strong>{truck.numero_economico}</strong>?
                Esta acción no se puede deshacer.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de generación de mantenimiento */}
      <AnimatePresence>
        {showMaintenanceModal && !isNewTruckMode && truck && (
          <MaintenanceModal 
            isOpen={showMaintenanceModal}
            onClose={(refresh) => {
              setShowMaintenanceModal(false);
              // Si se creó un mantenimiento y se solicitó actualizar
              if (refresh) {
                // Recargar los datos de la unidad
                const loadTruckData = async () => {
                  try {
                    const truckData = await trucksService.getById(id);
                    setTruck(truckData);
                  } catch (error) {
                    console.error('Error recargando datos de la unidad:', error);
                  }
                };
                loadTruckData();
              }
            }}
            truck={{
              ...truck,
              // Nos aseguramos de que estos campos críticos existan
              id: truck.id || null,
              serie: truck.serie || '',
              km_actual: truck.km_actual || 0
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TruckDetailPage;
